/**
 * @file     AddressStep.tsx
 * @purpose  Builder step 1 (design 11a, without BC Assessment): the street
 *           address from AddressAutocomplete, the unit typed by the owner,
 *           and the MLS® duplicate check by address key. Another brokerage's
 *           active listing blocks the step; Ray's own MLS® copy shows a note.
 */
'use client';

import '@/core/i18n/deferred/listings';
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { AddressAutocomplete, type StructuredAddress } from '@/components/forms/AddressAutocomplete';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import type { MlsDuplicateResult } from '@/services/listing-submissions/mlsDuplicate';
import type { MlsCheckState } from './stepModules';
import { CheckIcon, FieldError, WarnIcon, WhyLine, hubColors, radius } from '../ui';

export type AddressParts = { streetNumber: string; streetName: string; unit?: string; city: string; province: 'BC'; postalCode?: string };

const POSTAL = /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/;

/** Splits Photon's civic line into number and name; null when there is no house number or it is outside BC. */
export function addressFromSelection(sel: Pick<StructuredAddress, 'street' | 'unit' | 'city' | 'province' | 'postalCode'>): AddressParts | 'outside_bc' | null {
  const province = sel.province.trim().toLowerCase();
  if (province && province !== 'british columbia' && province !== 'bc') return 'outside_bc';
  const m = /^(\d+[A-Za-z]?)\s+(.{2,80})$/.exec(sel.street.trim());
  if (!m || !sel.city.trim()) return null;
  const postal = sel.postalCode.trim().toUpperCase();
  return {
    streetNumber: m[1],
    streetName: m[2].trim(),
    city: sel.city.trim(),
    province: 'BC',
    ...(sel.unit ? { unit: sel.unit.trim().slice(0, 12) } : {}),
    ...(POSTAL.test(postal) ? { postalCode: postal } : {}),
  };
}

export function addressLine(a: Record<string, unknown>): string {
  const civic = [a.streetNumber, a.streetName].filter((v) => typeof v === 'string' && v).join(' ');
  return [civic, a.city, a.province].filter((v) => typeof v === 'string' && v).join(', ');
}

type Props = {
  address: Record<string, unknown>;
  onAddress: (patch: Partial<AddressParts> & Record<string, unknown>, opts?: { immediate?: boolean }) => void;
  mls: MlsCheckState;
  mlsResult: MlsDuplicateResult | null;
  showErrors: boolean;
};

export function AddressStep({ address, onAddress, mls, mlsResult, showErrors }: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState(() => addressLine(address));
  const [error, setError] = useState<'not_found' | 'outside_bc' | null>(null);
  const unit = typeof address.unit === 'string' ? address.unit : '';
  const hasCivic = typeof address.streetNumber === 'string' && typeof address.streetName === 'string' && typeof address.city === 'string';

  useEffect(() => {
    if (!query && hasCivic) setQuery(addressLine(address));
  }, [address, hasCivic, query]);

  const onSelect = (sel: StructuredAddress) => {
    const parts = addressFromSelection(sel);
    if (parts === 'outside_bc') return setError('outside_bc');
    if (!parts) return setError('not_found');
    setError(null);
    setQuery(addressLine(parts));
    onAddress({ ...parts, unit: parts.unit ?? (unit || undefined) }, { immediate: true });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Box>
        <Box component="h1" sx={{ m: 0, fontSize: 28, fontWeight: fontWeightTokens.semibold, letterSpacing: '-0.012em' }}>{t('listings.owner.address.title')}</Box>
        <Box component="p" sx={{ m: 0, mt: 2, fontSize: 16, color: hubColors.textSecondary }}>{t('listings.owner.address.lede')}</Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 140px' }, gap: 3, alignItems: 'start' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <AddressAutocomplete
            value={query}
            onChange={(v) => {
              setQuery(v);
              if (error) setError(null);
            }}
            onSelect={onSelect}
            textFieldProps={{
              label: t('listings.owner.address.label'),
              placeholder: t('listings.owner.address.placeholder'),
              error: Boolean(error) || (showErrors && !hasCivic),
              inputProps: { 'aria-describedby': 'owner-address-help' },
            }}
          />
          {error ? (
            <FieldError id="owner-address-help">{t(error === 'outside_bc' ? 'listings.owner.address.outsideBc' : 'listings.owner.address.notFound')}</FieldError>
          ) : showErrors && !hasCivic ? (
            <FieldError id="owner-address-help">{t('listings.owner.address.notFound')}</FieldError>
          ) : (
            <WhyLine id="owner-address-help">{t('listings.owner.address.private')}</WhyLine>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            label={t('listings.owner.address.unit')}
            value={unit}
            onChange={(e) => onAddress({ unit: e.target.value.slice(0, 12) })}
            inputProps={{ maxLength: 12, 'aria-describedby': 'owner-unit-help' }}
          />
          <WhyLine id="owner-unit-help">{t('listings.owner.address.unitHelp')}</WhyLine>
        </Box>
      </Box>

      {hasCivic ? <MlsCheckCard state={mls} result={mlsResult} /> : null}
    </Box>
  );
}

function MlsCheckCard({ state, result }: { state: MlsCheckState; result: MlsDuplicateResult | null }) {
  const { t } = useTranslation();
  if (state === 'unknown') return null;
  const tone = state === 'blocked' ? hubColors.statusError : state === 'error' ? hubColors.statusWarning : state === 'checking' ? hubColors.textMuted : hubColors.statusSuccess;
  let title = t('listings.owner.address.checking');
  let line: string | null = null;
  if (state === 'none') {
    title = t('listings.owner.address.none');
    line = t('listings.owner.address.noneLine');
  } else if (state === 'blocked' && result?.status === 'blocked') {
    title = t('listings.owner.address.blocked');
    line = result.brokerageName
      ? t('listings.owner.address.blockedLine', { brokerage: result.brokerageName })
      : t('listings.owner.address.blockedLineNoBrokerage');
  } else if (state === 'linked') {
    title = t('listings.owner.address.linked');
    line = t('listings.owner.address.linkedLine');
  } else if (state === 'error') {
    title = t('listings.owner.address.checkFailed');
  }
  return (
    <Box
      role={state === 'blocked' ? 'alert' : 'status'}
      sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', p: 4, borderRadius: `${radius.lg}px`, border: `1px solid ${state === 'blocked' ? hubColors.statusError : hubColors.borderSubtle}`, backgroundColor: state === 'blocked' ? `color-mix(in srgb, ${hubColors.statusError} 8%, ${hubColors.surfaceCard})` : hubColors.surfaceCard }}
    >
      <Box component="span" sx={{ display: 'inline-flex', color: tone, mt: 0.25 }}>{state === 'blocked' || state === 'error' ? <WarnIcon size={18} /> : <CheckIcon size={18} />}</Box>
      <Box>
        <Box component="p" sx={{ m: 0, fontSize: 15, fontWeight: fontWeightTokens.semibold }}>{title}</Box>
        {line ? <Box component="p" sx={{ m: 0, mt: 1, fontSize: 14, color: hubColors.textSecondary, lineHeight: 1.5 }}>{line}</Box> : null}
      </Box>
    </Box>
  );
}
